import {IssueRelativeTime} from '@github-ui/ago'
import {announce} from '@github-ui/aria-live'
import {MarkdownViewer} from '@github-ui/markdown-viewer'
import {SandboxView} from '@github-ui/sandbox-view'
import {sanitize, type SanitizedHTML} from '@github-ui/sanitize'
import {useLocalStorage} from '@github-ui/use-safe-storage/local-storage'
import {useUser} from '@github-ui/use-user'
import {CodeIcon, EyeIcon, GearIcon} from '@primer/octicons-react'
import {AnchoredOverlay, IconButton, SegmentedControl, ToggleSwitch} from '@primer/react'
import {ScrollableRegion} from '@primer/react/experimental'
import {clsx} from 'clsx'
import {useCallback, useId, useLayoutEffect, useMemo, useRef, useState} from 'react'

import type {ScribeArtifactVersion} from '../types'
import styles from './ArtifactPanelContent.module.css'

export type ArtifactType = 'plan' | 'link' | 'embedded_html' | 'generic'

export type PanelArtifact = {
  id: string
  number?: number
  type: ArtifactType
  name: string
  body: string
  bodyHTML: string
  htmlUrl?: string
  trustedBodyHTML?: SanitizedHTML
  createdAt?: string
  updatedAt?: string
  hasEdits?: boolean
  updatedByActor?: {
    login: string
  }
  resourceType?: 'issue'
  resourceId?: string | number
  viewscreenUrl?: string
}

type ArtifactPanelBodyProps = {
  artifact: PanelArtifact
  selectedVersion?: ScribeArtifactVersion | null
}

/** Renders an artifact body as an HTML preview, Markdown, or plain text. */
export function ArtifactPanelBody({artifact, selectedVersion = null}: ArtifactPanelBodyProps) {
  const displayedBody = selectedVersion ? (selectedVersion.body ?? '') : artifact.body
  const displayedBodyHTML = selectedVersion ? (selectedVersion.body_html ?? '') : artifact.bodyHTML
  const trustedBodyHTML = selectedVersion ? (selectedVersion.body_html ?? undefined) : artifact.trustedBodyHTML
  const displayedAt = selectedVersion ? (selectedVersion.created_at ?? undefined) : artifact.updatedAt
  const displayedActor = selectedVersion ? (selectedVersion.actor ?? undefined) : artifact.updatedByActor
  const sanitizedHTML = useMemo(
    /* eslint-disable-next-line @github-ui/github-monorepo/no-custom-sanitize-config --
       Existing lax sanitizer usage; migrate to a named preset or VerifiedHTML when changed.
       This usage can probably migrate to the markdown preset. */
    () => trustedBodyHTML ?? (displayedBodyHTML ? sanitize(displayedBodyHTML) : ''),
    [displayedBodyHTML, trustedBodyHTML],
  )
  const viewscreenUrl = artifact.type === 'embedded_html' ? artifact.viewscreenUrl : undefined
  const selectedVersionNumber = selectedVersion?.version ?? null
  const {currentUser} = useUser()
  const userId = currentUser?.id
  const [storedDefaultToPreview, setDefaultToPreview] = useLocalStorage(
    `scribe-artifacts:default-to-preview:${userId ?? ''}`,
    false,
    {validator: value => typeof value === 'boolean'},
  )
  const [hasDiscoveredPreviewSettings, setHasDiscoveredPreviewSettings] = useLocalStorage(
    `scribe-artifacts:preview-settings-discovered:${userId ?? ''}`,
    false,
    {validator: value => typeof value === 'boolean'},
  )
  const defaultToPreview = Boolean(userId) && storedDefaultToPreview
  const [htmlPreviewSettingsOpen, setHTMLPreviewSettingsOpen] = useState(false)
  const htmlPreviewSettingsRef = useRef<HTMLButtonElement>(null)
  const htmlPreviewPreferenceLabelId = useId()
  const htmlPreviewPreferenceDescriptionId = useId()
  const htmlPreviewWarningId = useId()
  const htmlPreviewInstructionsId = useId()
  const htmlPreviewRef = useRef<HTMLDivElement>(null)
  const [htmlPreview, setHTMLPreview] = useState({
    artifactId: artifact.id,
    version: selectedVersionNumber,
    body: displayedBody,
    userId,
    defaultToPreview,
    active: defaultToPreview,
  })
  const htmlSourceChanged =
    htmlPreview.artifactId !== artifact.id ||
    htmlPreview.version !== selectedVersionNumber ||
    htmlPreview.body !== displayedBody ||
    htmlPreview.userId !== userId ||
    htmlPreview.defaultToPreview !== defaultToPreview

  if (htmlSourceChanged) {
    if (htmlPreview.artifactId !== artifact.id || htmlPreview.userId !== userId) {
      setHTMLPreviewSettingsOpen(false)
    }
    setHTMLPreview({
      artifactId: artifact.id,
      version: selectedVersionNumber,
      body: displayedBody,
      userId,
      defaultToPreview,
      active: defaultToPreview,
    })
  }
  const isHTMLPreviewActive = htmlPreview.active && !htmlSourceChanged

  useLayoutEffect(() => {
    if (isHTMLPreviewActive) {
      // The source scroll offset can hide the iframe beneath the sticky toolbar.
      htmlPreviewRef.current?.parentElement?.scrollTo(0, 0)
    }
  }, [isHTMLPreviewActive])

  return (
    <ScrollableRegion key={artifact.id} className={styles.Body} aria-label={artifact.name}>
      {displayedAt && (
        <div className={styles.Metadata}>
          <span className={styles.MetadataText}>
            <span>{selectedVersion ? 'Edited' : 'Updated'}</span> <IssueRelativeTime datetime={displayedAt} />
            {displayedActor && <span> by {displayedActor.login}</span>}
          </span>
        </div>
      )}
      {viewscreenUrl ? (
        <div ref={htmlPreviewRef} className={clsx(styles.HTMLPreview, isHTMLPreviewActive && styles.HTMLPreviewActive)}>
          <div className={styles.HTMLPreviewControls}>
            <SegmentedControl
              aria-label="HTML artifact view"
              onChange={index => {
                setHTMLPreview({
                  artifactId: artifact.id,
                  version: selectedVersionNumber,
                  body: displayedBody,
                  userId,
                  defaultToPreview,
                  active: index === 1,
                })
                if (index === 1 && userId && !hasDiscoveredPreviewSettings) {
                  setHasDiscoveredPreviewSettings(true, {flush: true})
                  setHTMLPreviewSettingsOpen(true)
                }
              }}
            >
              <SegmentedControl.IconButton aria-label="Source" icon={CodeIcon} selected={!isHTMLPreviewActive} />
              <SegmentedControl.IconButton
                aria-label="Preview"
                description="Previewing runs this code, which may send its contents to external sites."
                icon={EyeIcon}
                selected={isHTMLPreviewActive}
              />
            </SegmentedControl>
            <span id={htmlPreviewWarningId} className={styles.HTMLPreviewWarning}>
              Previewing runs this code, which may send its contents to external sites.
            </span>
            {userId && (
              <>
                <IconButton
                  ref={htmlPreviewSettingsRef}
                  icon={GearIcon}
                  variant="invisible"
                  size="small"
                  aria-label="HTML preview settings"
                  aria-haspopup="dialog"
                  aria-expanded={htmlPreviewSettingsOpen}
                  onClick={() => setHTMLPreviewSettingsOpen(open => !open)}
                />
                {htmlPreviewSettingsOpen && (
                  <AnchoredOverlay
                    open
                    onClose={() => setHTMLPreviewSettingsOpen(false)}
                    renderAnchor={null}
                    anchorRef={htmlPreviewSettingsRef}
                    align="end"
                    width="small"
                    preventOverflow={false}
                    focusZoneSettings={{disabled: true}}
                    cssAnchorPositioningSettings={{disable: true}}
                    overlayProps={{role: 'dialog', 'aria-label': 'HTML preview settings'}}
                  >
                    <div className={styles.HTMLPreviewSettings}>
                      <div className={styles.HTMLPreviewPreference}>
                        <span id={htmlPreviewPreferenceLabelId}>Show preview by default</span>
                        <ToggleSwitch
                          className={styles.HTMLPreviewToggle}
                          size="small"
                          checked={defaultToPreview}
                          onChange={checked => setDefaultToPreview(checked, {flush: true})}
                          aria-labelledby={htmlPreviewPreferenceLabelId}
                          aria-describedby={`${htmlPreviewPreferenceDescriptionId} ${htmlPreviewWarningId}`}
                        />
                      </div>
                      <p id={htmlPreviewPreferenceDescriptionId} className={styles.HTMLPreviewSettingsDescription}>
                        Automatically run HTML artifacts when you open them.
                      </p>
                    </div>
                  </AnchoredOverlay>
                )}
              </>
            )}
          </div>
          {isHTMLPreviewActive ? (
            <>
              <div className={styles.HTMLPreviewAttribution}>Artifact HTML preview.</div>
              <span id={htmlPreviewInstructionsId} className="sr-only">
                If keyboard focus becomes trapped, use your browser&apos;s focus-navigation shortcut (usually F6) to
                leave the preview.
              </span>
              <div className={styles.HTMLPreviewFrame}>
                <SandboxView
                  key={selectedVersion?.version ?? artifact.id}
                  content={displayedBody}
                  restrictPermissions
                  title={`HTML preview: ${artifact.name}`}
                  aria-describedby={htmlPreviewInstructionsId}
                  viewscreenUrl={viewscreenUrl}
                />
              </div>
            </>
          ) : (
            <>
              <div className={styles.HTMLPreviewAttribution}>Artifact HTML source</div>
              <pre className={styles.HTMLSource}>{displayedBody}</pre>
            </>
          )}
        </div>
      ) : sanitizedHTML ? (
        <div className={styles.MarkdownContent}>
          <MarkdownViewer verifiedHTML={sanitizedHTML} />
        </div>
      ) : (
        <pre className={styles.PlainBody}>{displayedBody}</pre>
      )}
    </ScrollableRegion>
  )
}

export function useArtifactVersionSelection(artifact: PanelArtifact | null) {
  const [selection, setSelection] = useState<{artifactId: string; version: ScribeArtifactVersion} | null>(null)
  const artifactId = artifact?.id
  const artifactName = artifact?.name
  const selectedVersion = selection && selection.artifactId === artifactId ? selection.version : null
  const selectVersion = useCallback(
    (version: ScribeArtifactVersion) => {
      if (!artifactId) return

      setSelection({artifactId, version})
      announce(
        artifactName ? `Showing version ${version.version} of ${artifactName}.` : `Showing version ${version.version}.`,
      )
    },
    [artifactId, artifactName],
  )

  return {selectedVersion, selectVersion}
}
